import { useRef } from "react";
import type { Preferences } from "../hooks/usePreferences";
import { useModalFocus } from "../hooks/useModalFocus";

interface SettingsPanelProps {
  preferences: Preferences;
  onChange: (patch: Partial<Preferences>) => void;
  onClose: () => void;
}

export function SettingsPanel({ preferences, onChange, onClose }: SettingsPanelProps) {
  const panelRef = useRef<HTMLElement>(null);
  useModalFocus(panelRef, true, onClose);

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section ref={panelRef} className="settings-panel" role="dialog" aria-modal="true" aria-labelledby="settings-title" tabIndex={-1}>
        <header>
          <div><p className="eyebrow"><span /> Comfort</p><h2 id="settings-title">Game settings</h2></div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close settings">×</button>
        </header>
        <div className="settings-list">
          <SettingToggle
            label="Game audio"
            description="Procedural impacts, countdowns, and goals"
            checked={preferences.audio}
            onChange={(audio) => onChange({ audio })}
          />
          <SettingToggle
            label="Reduce motion"
            description="Removes trails and most particles"
            checked={preferences.reducedMotion}
            onChange={(reducedMotion) => onChange({ reducedMotion })}
          />
          <SettingToggle
            label="High contrast"
            description="Uses white and yellow gameplay outlines"
            checked={preferences.highContrast}
            onChange={(highContrast) => onChange({ highContrast })}
          />
          <label className="range-setting">
            <span><strong>Touch offset</strong><small>Moves the mallet above your finger</small></span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={preferences.touchOffset}
              onChange={(event) => onChange({ touchOffset: Number(event.target.value) })}
            />
          </label>
        </div>
        <p className="settings-help">During play, use the pointer, touch, a gamepad, WASD, ZQSD, or arrow keys. Important states also use text, not color alone.</p>
        <button className="primary-button primary-button--small" type="button" onClick={onClose}>Done</button>
      </section>
    </div>
  );
}

interface SettingToggleProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function SettingToggle({ label, description, checked, onChange }: SettingToggleProps) {
  return (
    <label className="toggle-setting">
      <span><strong>{label}</strong><small>{description}</small></span>
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <i aria-hidden="true" />
    </label>
  );
}
